/*
 * Copyright(c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */
import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { UpgradeModule } from '@angular/upgrade/static';

import { environment } from '@env/environment';
import { ShellModule } from '@app/shell/shell.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { WindowRef } from '@app/common/window-ref.service';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ModalsModule } from '@core/lib/components/modal/modals.module';
import { CoreModule } from '@app/core/core.module';
import { ModalComponentsModule } from '@app/common/modals/components/modal-components.module';
import { ComponentsModule } from '../../../core/src';
import { AuthenticationModule } from '@app/authentication/authentication.module';
import { AppDataResolver } from './app.data.resolver';
import { ExamSharingPageComponent } from '@app/di/exam-sharing/exam-sharing-page.component';

export function HttpLoaderFactory(http: HttpClient) {
  return new MultiTranslateHttpLoader(http, [
    { prefix: '/assets/i18n/', suffix: '.json' },
    { prefix: '/assets/i18n/core/', suffix: '.json' },
    { prefix: '/assets/i18n/virtual-grid/', suffix: '.json' },
    { prefix: '/assets/i18n/record-management/', suffix: '.json' }
  ]);
}

@NgModule({
  declarations: [AppComponent, ExamSharingPageComponent],
  imports: [
    BrowserModule,
    //ServiceWorkerModule.register('./ngsw-worker.js', { enabled: environment.production }),
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    AngularSvgIconModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    CoreModule,
    ShellModule,
    AuthenticationModule,
    ModalsModule,
    UpgradeModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      closeButton: false,
      positionClass: 'toast-top-right',
      timeOut: 7000,
      extendedTimeOut: 1000
    }),
    ComponentsModule,
    ModalComponentsModule,
    AppRoutingModule // must be imported as the last module as it contains the fallback route
  ],
  entryComponents: [],
  providers: [
    WindowRef,
    AppDataResolver,
    {
      provide: APP_INITIALIZER,
      useFactory: appInit,
      multi: true,
      deps: [AppDataResolver]
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(private winRef: WindowRef) {
    this.winRef.nativeWindow.proxyPath = environment.proxyPath;
  }
}

export function appInit(appDataResolver: AppDataResolver) {
  return () => appDataResolver.loadData();
}
